const MAX_EDGE = 2048;
const TARGET_BYTES = 8 * 1024 * 1024;
// Re-encoding a photo already small enough for a fast mobile upload adds a
// noticeable pause on older phones, without meaningfully reducing transfer
// time. Larger camera originals still go through the resize path below.
const DIRECT_UPLOAD_BYTES = 2 * 1024 * 1024;

function isSupportedPhotoType(type: string) {
  return type === "image/jpeg" || type === "image/png" || type === "image/webp";
}

function encodeJpeg(canvas: HTMLCanvasElement, quality: number) {
  return new Promise<Blob | null>((resolve) =>
    canvas.toBlob(resolve, "image/jpeg", quality),
  );
}

function fileNameForJpeg(name: string) {
  const base = name.replace(/\.[^.]+$/, "").replace(/[^\w.-]+/g, "_").slice(0, 180);
  return `${base || "photo"}.jpg`;
}

/**
 * Phone cameras often hand the field app HEIC, an empty MIME type, or a
 * full-resolution JPEG larger than the API will accept. Decode and rewrite
 * as a JPEG the job record can store.
 */
export async function prepareFieldPhoto(file: File): Promise<File> {
  if (isSupportedPhotoType(file.type) && file.size <= DIRECT_UPLOAD_BYTES) {
    return file;
  }

  if (typeof createImageBitmap !== "function") {
    if (isSupportedPhotoType(file.type)) return file;
    throw new Error(
      "This phone photo format isn’t supported. Take the picture again, or set Camera > Formats to Most Compatible.",
    );
  }

  let bitmap: ImageBitmap;
  try {
    bitmap = await createImageBitmap(file);
  } catch {
    if (isSupportedPhotoType(file.type) && file.size <= TARGET_BYTES) return file;
    throw new Error(
      "This phone photo format isn’t supported. Take the picture again, or set Camera > Formats to Most Compatible.",
    );
  }

  try {
    const scale = Math.min(1, MAX_EDGE / Math.max(bitmap.width, bitmap.height));
    const width = Math.max(1, Math.round(bitmap.width * scale));
    const height = Math.max(1, Math.round(bitmap.height * scale));
    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext("2d");
    if (!ctx) {
      if (isSupportedPhotoType(file.type)) return file;
      throw new Error("That photo could not be prepared for upload.");
    }
    ctx.drawImage(bitmap, 0, 0, width, height);
    const blob = await encodeJpeg(canvas, file.size > TARGET_BYTES ? 0.7 : 0.85);
    if (!blob) throw new Error("That photo could not be prepared for upload.");
    return new File([blob], fileNameForJpeg(file.name), { type: "image/jpeg" });
  } finally {
    bitmap.close();
  }
}
